import { useMutation, useQueryClient } from '@tanstack/react-query'

import { getServerLikesActive } from '@wwt/core/admin'
import { Offer } from '@wwt/entities/search-response'

import { getFullRoomId } from '@/entities/offer/index.ts'
import { assert } from '@/shared/lib/assert.ts'

import {
	DislikeKeys,
	DislikeTypes,
	dislikeKeys
} from '../config/dislikeQueryKeys.ts'
import { undislikeQuery } from '../query/undislikeQuery.ts'
import { DislikeMutationUtils } from '../utils/dislikeMutationUtils.ts'

export const useUndislikeMutation = () => {
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: dislikeKeys.delete(),
		mutationFn: async (offer: Offer) => {
			if (!getServerLikesActive()) {
				return
			}

			return await undislikeQuery(offer.IDs)
		},
		onMutate: async (offer: Offer) => {
			const keys = {
				dislikes: dislikeKeys.all()
			}

			/* prettier-ignore */
			const previous = {
				dislikes: queryClient.getQueryData<DislikeTypes['all'], DislikeKeys['all'] >(keys.dislikes)
			}

			const isDisliked = Boolean(
				previous.dislikes &&
					previous.dislikes.disliked.includes(getFullRoomId(offer))
			)

			assert(
				isDisliked === true,
				'Offer should be disliked for call undislike mutation'
			)

			await queryClient.cancelQueries({ queryKey: keys.dislikes })

			queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(
				keys.dislikes,
				saved => DislikeMutationUtils.removeOfferFromDisliked(saved, offer)
			)

			return {
				keys,
				previous
			}
		},

		/* prettier-ignore */
		onError: (_err, _disliked, context) => {
			assert(context !== undefined, 'onError context not found in undislike mutation')

			queryClient.setQueryData<DislikeTypes['all'], DislikeKeys['all']>(context.keys.dislikes, context.previous.dislikes)
		},

		/* prettier-ignore */
		onSettled: async (_, _err, _offer, context) => {
			assert(context !== undefined, 'onSettled context not found in undislike mutation')

			await queryClient.cancelQueries({ queryKey: context.keys.dislikes })

			await queryClient.invalidateQueries({ queryKey: context.keys.dislikes })
		}
	})
}
